/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2020, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'react',
	'react-dom',
	'prop-types',
	'@ba/ba-ui-carbon-toolkit',
	'../../dialogs/commonSelectItemsDialog/CommonSelectItemsComponent',
	'../../../utils/Flyout',
	'@ba/ba-graphics/dist/icons-js/caret--right_32',
	'jquery',
	'../../../lib/@waca/core-client/js/core-client/utils/JQueryTabbable'
], function(React, ReactDOM, PropTypes, CarbonToolkit, CommonSelectItemsComponent, Flyout, IconChevron, $) {

	const { Button } = CarbonToolkit;

	class SubMenu extends React.Component {
		constructor(props) {
			super(props);
			this.myRef = props.myRef || React.createRef();
		}

		_selectSubMenu(name) {
			this.props.setSelectedSubMenu(name);

			if(!this.flyout) {
				this.flyout = new Flyout({
					selector: this.myRef.current._node,
					placement: this.props.itemSpec.placement || 'right',
					parent: this.myRef.current._node.parentNode,
					viewInstance: {
						getRenderedHtml: () => {
							this._$flyoutContent = $('<div/>');
							ReactDOM.render(this._renderCommonSelectItems(), this._$flyoutContent[0]);
							return this._$flyoutContent;
						},
						setFocus: () => {
							this._$flyoutContent.find(':focusable').focus();
						},
						destroy: () => {
							delete this._$flyoutContent;
						}
					}
				});
			}
			this.flyout.open();
		}

		_renderCommonSelectItems() {
			const itemSpec = this.props.itemSpec;
			const subMenuViewOption = this.props.itemSpec.view;
			const possibleItems = subMenuViewOption.state.possibleItems;
			const selectedItem = subMenuViewOption.state.getCurrentSelectedItem && subMenuViewOption.state.getCurrentSelectedItem() || null;
			const actionApplyCb = itemSpec.actions.apply;
			return (
				<CommonSelectItemsComponent
					possibleItems={possibleItems}
					currentlySelectedItemId={selectedItem}
					selectionHandler={(id)=>{
						// apply the action and hide toolbar
						actionApplyCb(id);
						// update the flyout if needed
						if (subMenuViewOption.state.updateFlyout) {
							this.props.nextViewCb(this.props.itemSpec, { currentTarget: this.myRef.current });
						}
					}}
					onKeyDown={(evt) => this._handleSubMenuKeyDown(evt)}>
				</CommonSelectItemsComponent>
			);
		}

		_buttonSelected() {
			/**
			 * the parent will control the enabler for the subMenu so that we wont show two subMenus at the same time
			 */
			if (this.props.itemSpec.name === this.props.selectedSubMenu) {
				this.props.setSelectedSubMenu(null);
			} else {
				this._selectSubMenu(this.props.itemSpec.name);
			}
		}

		_handleMouseEnter() {
			this._selectSubMenu(this.props.itemSpec.name);
		}

		_handleKeyDown(evt) {
			if(evt.keyCode === 32 || evt.keyCode === 13 || evt.keyCode === 39) {
				this._buttonSelected(evt);
			}
		}

		_handleSubMenuKeyDown(evt) {
			const placement = this.flyout && this.flyout.getPlacement();
			if ((placement === 'left' && evt.keyCode === 39) || (placement === 'right' && evt.keyCode === 37)) {
				this.myRef.current.focus();
				this._buttonSelected(evt);
			}
		}

		_renderCoachMark() {
			if(!this.props.itemSpec.coachMark || !this.props.itemSpec.coachMark.dashboard) {
				return;
			}

			const dashboard = this.props.itemSpec.coachMark.dashboard;
			const coachMark = dashboard.getFeature('CoachMark');
			if (!coachMark) {
				return;
			}

			const coachMarkOptions = {
				id: this.props.itemSpec.coachMark.id,
				title: this.props.itemSpec.coachMark.title,
				contents: this.props.itemSpec.coachMark.contents,
				placement: 'bottom',
				domElement: this.myRef.current._node
			};
			dashboard.prepareGlassOptions(coachMarkOptions);
			coachMark.addCoachMark(coachMarkOptions);
		}

		_removeDecorator() {
			if (this._decorator) {
				this._decorator.remove();
				this._decorator = null;
			}
		}

		componentDidMount() {
			if(this.props.itemSpec.callbacks) {
				this.props.itemSpec.callbacks.registerCallback(this);
			}

			this._renderCoachMark();
		}

		componentWillUnmount() {
			if(this.props.itemSpec.callbacks) {
				this.props.itemSpec.callbacks.unregisterCallback();
			}
			if(this.flyout) {
				this.flyout.destroy();
				delete this.flyout;
			}
			this._removeDecorator();
		}

		_getButtonClass() {
			let classList = [];
			if(this.props.itemSpec.selected) {
				classList.push('selected');
			}
			if(this.props.itemSpec.className) {
				classList.push(this.props.itemSpec.className);
			}
			return classList.join(' ');
		}

		render() {
			const isButtonDisabled = () => {
				if (!this.props.itemSpec.disabled) {
					return false;
				} else {
					return (typeof this.props.itemSpec.disabled === 'function') ? this.props.itemSpec.disabled() : true;
				}
			};
			const buttonClass = this._getButtonClass();
			const iconID = this.props.itemSpec.useIcon ? this.props.itemSpec.icon : IconChevron.default.id;

			if(this.flyout && this.props.itemSpec.name !== this.props.selectedSubMenu) {
				this.flyout.close();
			}

			return (
				<div>
					<Button
						tabIndex={0}
						walkme-data-id={this.props.itemSpec.name}
						ref={this.myRef}
						key={this.props.itemSpec.name}
						title={this.props.itemSpec.label}
						label={!this.props.toolbarOptions || !this.props.toolbarOptions.iconOnly ? this.props.itemSpec.text : undefined}
						icon={ !this.props.toolbarOptions || !this.props.toolbarOptions.iconOnly ? iconID : null }
						size='field'
						kind='ghost'
						autofocus={this.props.itemSpec.focus}
						ellipsis={true}
						disabled={isButtonDisabled()}
						aria-label={this.props.itemSpec.label}
						className={buttonClass}
						onKeyDown={(evt) => this._handleKeyDown(evt)}
						onClick={(evt) => this._buttonSelected(evt)}
						onMouseEnter={() => this._handleMouseEnter()}
					/>
				</div>
			);
		}
	}

	SubMenu.propTypes = {
		itemSpec: PropTypes.array,
		icon: PropTypes.string,
		focus: PropTypes.boolean,
		updateButton: PropTypes.function,
		toolbarOptions: PropTypes.object,
		selectedSubMenu: PropTypes.string,
		setSelectedSubMenu: PropTypes.function,
		toolbar: PropTypes.object,
		nextViewCb: PropTypes.function,
		myRef: PropTypes.object
	};

	return SubMenu;
});
